import { Auth0Provider } from "@auth0/auth0-react";
import FullPageSpinner from "@/components/FullPageSpinner/index.ts";
import MultiProvider from "@/components/MultiProvider/index.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch";
import "@/utils/zod-config.ts";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { CssBaseline, createTheme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { SnackbarProvider } from "notistack";
import { type ReactNode, StrictMode } from "react";
import { ConfirmProvider } from "react-confirm-hook";
import { createRoot } from "react-dom/client";
import useAuth0LoadPromise, { type Auth0LoadPromise } from "@/hooks/useAuth0LoadPromise.ts";
import {
    createQueryOptionsFactory,
    type QueryOptionsFactory,
    QueryOptionsFactoryProvider,
} from "@/queries";
import { routeTree } from "@/routeTree.gen.ts";
import { extendedReplaceEqualDeep } from "@/utils/api.ts";

const container = document.getElementById("root");

if (!container) {
    throw new Error("Root element missing");
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            structuralSharing: extendedReplaceEqualDeep,
            retry: false,
        },
    },
});

const root = createRoot(container);

const router = createRouter({
    routeTree,
    defaultPendingComponent: FullPageSpinner,
    context: {
        queryClient,
        // Injected through React
        qof: undefined as unknown as QueryOptionsFactory,
        auth0LoadPromise: undefined as unknown as Auth0LoadPromise,
    },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

const App = (): ReactNode => {
    const authFetch = useAuthenticatedFetch();
    const queryOptionsFactory = createQueryOptionsFactory(authFetch);
    const auth0LoadPromise = useAuth0LoadPromise();

    return (
        <QueryOptionsFactoryProvider factory={queryOptionsFactory}>
            <RouterProvider
                router={router}
                context={{
                    qof: queryOptionsFactory,
                    auth0LoadPromise: auth0LoadPromise,
                }}
            />
        </QueryOptionsFactoryProvider>
    );
};

const theme = createTheme({
    palette: {
        background: {
            default: "#f3f3f3",
            paper: "#ffffff",
        },
        primary: {
            main: "#802f2D",
        },
        secondary: {
            main: "#497b55",
        },
    },
    components: {
        MuiStack: {
            defaultProps: {
                useFlexGap: true,
            },
        },
    },
});

root.render(
    <StrictMode>
        <MultiProvider
            providerCreators={[
                (children) => (
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        {children}
                    </ThemeProvider>
                ),
                (children) => (
                    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
                ),
                (children) => (
                    <Auth0Provider
                        domain={import.meta.env.VITE_APP_AUTH0_DOMAIN}
                        clientId={import.meta.env.VITE_APP_AUTH0_CLIENT_ID}
                        authorizationParams={{
                            redirect_uri: window.location.origin,
                            audience: import.meta.env.VITE_APP_AUTH0_AUDIENCE,
                        }}
                        onRedirectCallback={(appState) => {
                            router.history.replace(appState?.returnTo ?? "/");
                        }}
                        useRefreshTokens
                        cacheLocation="localstorage"
                    >
                        {children}
                    </Auth0Provider>
                ),
                (children) => <SnackbarProvider>{children}</SnackbarProvider>,
                (children) => <ConfirmProvider>{children}</ConfirmProvider>,
                (children) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        {children}
                    </LocalizationProvider>
                ),
            ]}
        >
            <App />
        </MultiProvider>
    </StrictMode>,
);
